import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { CampaignOverviewCard } from './shared/CampaignOverviewCard';
import { ProgressBarCampaign } from './shared/ProgressBarCampaign';
import { StepperRouting } from './shared/StepperRouting';
import { CategoryMultiSelect } from './shared/CategoryMultiSelect';
import { CampaignTypeCard } from './shared/CampaignTypeCard';
import { BriefingService } from '../service';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../utils/toasters';
import { IParams } from '../../../../../model/shared/IParams';
import { IBudgedRange, ICreatorTypes } from '../../../../../model/briefing/step1/ICreatorTypes';
import { IMainPlatforms } from '../../../../../model/briefing/step1/IMainPlatforms';
import { useTranslation } from 'react-i18next';
import { hasAccessRights, oneStepUp } from '../../../../../shared/functions/Functions';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { ICampaignGoals } from '../dto/ICreateCampaign';
import TooltipPackage from '../../../../shared/packages/components/TooltipPackage';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';

export const StepOne = () => {
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { company } = useSelector((state: IAllStates) => state.auth.user);
    const [status, setStatus] = useState('');
    const [isNextClicked, setIsNextClicked] = useState(false);
    const [creatorTypes, setCreatorTypes] = useState<ICreatorTypes[]>([]);
    const [allPlatforms, setAllPlatforms] = useState<IMainPlatforms[]>([]);
    const [isBriefingComplete, setIsBriefingComplete] = useState(true);
    const [budgetRange, setBudgetRange] = useState<IBudgedRange>();
    const language = useSelector((state: IAllStates) => state.auth.user.locale);
    // const [budgetRangeNumbers, setBudgetRangeNumbers] = useState<number[]>([]);
    const [isConfirmOpened, setIsConfirmOpened] = useState(false);
    const isSomePlatformSelected = allPlatforms?.some(platform => platform?.isSelected);
    const isTypeSelected = creatorTypes?.some(type => type.isSelected);
    const [goals, setGoals] = useState<ICampaignGoals[]>([]);
    const [selectedGoal, setSelectedGoal] = useState<ICampaignGoals>();
    const [isCharity, setIsCharity] = useState(false);

    const selectPlatformHandler = (platformId: number) => {
        if (isTypeSelected) {
            const copy = [...allPlatforms];
            const indexToReplace = copy?.findIndex(item => item?.id === platformId);
            copy[indexToReplace] = {
                ...copy[indexToReplace],
                isSelected: !copy[indexToReplace]?.isSelected,
            };
            setAllPlatforms(copy);
        } else {
            WarningToast(`${t('general.alert.warning_select_type')}`);
        }
    };

    const selectCreatorTypeHandler = (id: number) => {
        const copy = [...creatorTypes];
        const indexToReplace = copy?.findIndex(item => item?.id === id);
        copy[indexToReplace] = {
            ...copy[indexToReplace],
            isSelected: !copy[indexToReplace]?.isSelected,
        };
        const otherElements = copy?.filter(item => item?.id !== id);
        otherElements?.forEach(item => {
            // set all other types false value
            item.isSelected = false;
        });
        setCreatorTypes(copy);
    };

    const submitStepHandler = () => {
        const creatorType = creatorTypes?.find(creator => creator?.isSelected);
        const mainPlatforms = allPlatforms?.filter(platform => platform?.isSelected);
        const combinedData = {
            creatorType,
            mainPlatforms,
            campaignGoal: selectedGoal,
            isCharity,
        };

        setIsNextClicked(true);
        if (id) {
            BriefingService.updateStepOne(id, combinedData)
                .then(response => {
                    if (response) {
                        oneStepUp(navigate, id ?? '', response.isBriefingComplete, response.briefingStep);
                        SuccessToast(`${t('general.alert.success_updated_campaign_step')}`);
                    }
                })
                .catch(error => ErrorToast(error))
                .finally(() => {
                    setIsNextClicked(false);
                });
        }
    };

    useEffect(() => {
        if (id) {
            BriefingService.getBriefingStepOne(id)
                .then(response => {
                    const { creatorTypes, mainPlatforms } = response;
                    setAllPlatforms(mainPlatforms);
                    const activeCreatorTypes = creatorTypes?.filter((type: ICreatorTypes) => type.isActive);
                    setCreatorTypes(
                        activeCreatorTypes.map((type: ICreatorTypes, index: number) => {
                            return {
                                ...type,
                                isSelected: creatorTypes.every((item: ICreatorTypes) => !item.isSelected)
                                    ? index === 0
                                    : type.isSelected,
                            };
                        })
                    );
                    setStatus(response?.status);
                    setIsBriefingComplete(response.isBriefingComplete);
                    setGoals(response?.goals?.data);
                    setSelectedGoal(
                        response?.goals?.data?.find((item: ICampaignGoals) => item?.isSelected) ??
                            response?.goals?.data[0]
                    );
                    setIsCharity(response?.isCharity);
                    console.log(response);
                })
                .catch(error => ErrorToast(error));
        }
    }, [id]);

    useEffect(() => {
        if (creatorTypes) {
            const numberOfSelectedPlatforms = allPlatforms?.filter(item => item.isSelected)?.length;
            const copy = [...creatorTypes];
            const selected = copy?.find(item => item.isSelected);
            const selectedBudgetRange = selected?.budgetRanges[numberOfSelectedPlatforms - 1];
            setBudgetRange(selectedBudgetRange);
            // if (numberOfSelectedPlatforms) {
            //     setBudgetRangeNumbers([
            //         selectedBudgetRange?.recommendedMinBudget as number,
            //         selectedBudgetRange?.recommendedMaxBudget as number,
            //     ]);
            // }
        }
    }, [JSON.stringify(allPlatforms), creatorTypes]);

    const handleChangesGoals = (event: React.ChangeEvent<HTMLInputElement>) => {
        const goalId = Number(event.target.value);
        const selectedGoal = goals?.find(item => item.id === goalId);
        if (selectedGoal) {
            selectedGoal.isSelected = !selectedGoal.isSelected;
            setSelectedGoal(selectedGoal);
        }
    };

    const handleChangesPaymentType = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const booleanValue = value === 'true';
        setIsCharity(booleanValue);
    };
    return (
        <>
            <ConfirmationModal
                title={t('general.confirm_selection')}
                description={t('general.selectMediaPlatform')}
                showSuccessButton={false}
                show={isConfirmOpened}
                closeModal={() => setIsConfirmOpened(false)}
            />
            <Row className="mb-5 mb-md-0">
                <Col md={8}>
                    {!isBriefingComplete ? (
                        <div className="mb-4">
                            <ProgressBarCampaign title={t('general.tabs.step1')} percent={5} />
                        </div>
                    ) : null}

                    <CardLayout>
                        <Col md={12} className="mb-4">
                            <div>
                                <h6>{t('brand.campaign.briefing.campaign_goals')}</h6>
                                <p>{t('brand.campaign.briefing.campaign_goals_desc')}</p>
                            </div>
                            <Row className="flex-column-reverse flex-md-row">
                                <Col md={3}>
                                    <div>
                                        <CustomImageHandler
                                            classes="img-cover w-100 default-radius"
                                            thumbnailPath={selectedGoal?.goalImage?.thumbnail}
                                            photoPath={selectedGoal?.goalImage.thumbnail ?? ''}
                                            altTag={selectedGoal?.goalImage?.title ?? ''}
                                        />
                                    </div>
                                </Col>
                                <Col md={8} className="pt-2 pt-md-0">
                                    <FormControl>
                                        <RadioGroup
                                            row
                                            aria-labelledby="demo-row-radio-buttons-group-label"
                                            name="row-radio-buttons-group"
                                            onChange={handleChangesGoals}
                                            value={selectedGoal?.id ?? 0}
                                        >
                                            {goals.map(goal => (
                                                <FormControlLabel
                                                    value={goal.id}
                                                    key={goal?.id}
                                                    control={<Radio />}
                                                    label={goal.title}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                    <p>{language === 'en' ? selectedGoal?.story : selectedGoal?.storyGerman}</p>
                                </Col>
                            </Row>
                        </Col>

                        <div>
                            <h5>{t('brand.campaign.briefing.step1.creator_type')}</h5>
                            <p className="text-muted">{t('brand.campaign.briefing.step1.creator_type_description')}</p>
                            <Row>
                                {creatorTypes?.map(item => (
                                    <div
                                        className="my-2 col-md-4"
                                        key={item?.id}
                                        onClick={() => selectCreatorTypeHandler(item?.id)}
                                    >
                                        <CampaignTypeCard item={item} />
                                    </div>
                                ))}
                            </Row>
                            <div className="mt-3">
                                <h5>{t('brand.campaign.briefing.step1.social_media_platform')}</h5>
                                <p className="text-muted">
                                    {t('brand.campaign.briefing.step1.social_media_platform_description')}
                                </p>
                            </div>
                            <div className="d-flex flex-wrap">
                                {allPlatforms?.map(item => (
                                    <CategoryMultiSelect
                                        item={item}
                                        key={item?.id}
                                        isActive={item?.isSelected}
                                        clickHandler={() => selectPlatformHandler(item?.id)}
                                    />
                                ))}
                            </div>
                            <div className={`mt-3 ${isCharity && 'mb-5'}`}>
                                <h5>{t('brand.campaign.briefing.step1.payment_type')}</h5>
                                <p className="text-muted">{t('brand.campaign.briefing.step1.payment_type_desc')}</p>

                                <FormControl>
                                    <RadioGroup
                                        row
                                        aria-labelledby="demo-row-radio-buttons-group-label"
                                        name="row-radio-buttons-group"
                                        onChange={handleChangesPaymentType}
                                        value={isCharity}
                                    >
                                        <FormControlLabel
                                            value={false}
                                            key="paidCampaign"
                                            control={<Radio />}
                                            label={t('brand.account.price_list_modal.paid_campaign')}
                                        />
                                        <TooltipPackage
                                            show={!company?.package?.allowBarterDeals}
                                            trigger="click"
                                            description={'noBarterDeal'}
                                        >
                                            <FormControlLabel
                                                disabled={!company?.package?.allowBarterDeals}
                                                value={true}
                                                key="bartedDeal"
                                                control={<Radio />}
                                                label={
                                                    <div className="d-flex align-items-center">
                                                        {t('brand.account.price_list_modal.barter_deals')}
                                                        <TooltipOverlay
                                                            placement="right"
                                                            className="ms-2"
                                                            content={
                                                                <div className="d-flex flex-column">
                                                                    {t(
                                                                        'brand.account.price_list_modal.barter_deals_desc'
                                                                    )}
                                                                </div>
                                                            }
                                                        >
                                                            <HiOutlineInformationCircle size={20} />
                                                        </TooltipOverlay>
                                                    </div>
                                                }
                                            />
                                        </TooltipPackage>
                                    </RadioGroup>
                                </FormControl>
                            </div>
                            {!!isCharity ||
                                (hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE) && (
                                    <>
                                        <div className="mt-3">
                                            <h6>{t('brand.campaign.briefing.step1.budged_range')}</h6>
                                            <p className="text-muted">
                                                {t('brand.campaign.briefing.step1.budged_range_description')}
                                            </p>
                                        </div>
                                        <div className="mb-4">
                                            <h5 className="text-primary">
                                                {t('brand.campaign.briefing.overview_card.budget_range')}:
                                                <span className="euro-symbol h5">
                                                    {priceSplitter(budgetRange?.minPrice ?? 0)}
                                                </span>{' '}
                                                -{' '}
                                                <span className="h5 euro-symbol">
                                                    {priceSplitter(budgetRange?.maxPrice ?? 0)}
                                                </span>
                                            </h5>
                                        </div>
                                    </>
                                ))}
                            <div>
                                <StepperRouting
                                    submitStepHandler={() => {
                                        if (isSomePlatformSelected) {
                                            submitStepHandler();
                                        } else {
                                            setIsConfirmOpened(true);
                                        }
                                    }}
                                    isNextDisabled={isNextClicked}
                                    status={status}
                                />
                            </div>
                        </div>
                    </CardLayout>
                </Col>
                <Col md={4} className="pr-md-4">
                    <CampaignOverviewCard />
                </Col>
            </Row>
        </>
    );
};
